import { isMemoryArchiveType } from "../leaderMemoryVisibility.ts";
import type { GraphSnapshot } from "../contextGraph.ts";
import { buildGraphBehavioralState } from "../behaviorSubstrate.ts";

/** The server-governed context is the authority. This type is a ranking projection only. */
export interface BiRetrievalTheme {
  id: string;
  behaviorKeys: string[];
  status: string;
  confidence: number;
  lastSeenAt?: string | null;
  occurrenceCount?: number | null;
  governance?: "confirmed" | "rejected" | null;
  leaderSafe: boolean;
  contradicted?: boolean;
  contradictionProbe?: string;
}
export interface BiEvidenceCandidate<T> {
  item: T;
  id: string;
  score: number;
  occurredAt?: string | null;
  behaviorKeys?: string[];
  linkedThemeIds?: string[];
  contradicted?: boolean;
}
export const normalizeBiConfidence = (level: unknown): number =>
  level === "high" ? 1 : level === "medium" ? 0.6 : level === "low" ? 0.3 : 0;
export const normalizeBiKey = (key: string): string => key.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
export function isBiThemeRetrievalActive(theme: BiRetrievalTheme): boolean {
  if (theme.governance === "rejected" || theme.contradicted) return false;
  return theme.status === "active" || (theme.governance === "confirmed" && ["emerging", "monitoring"].includes(theme.status));
}

/** All candidates MUST already have passed source visibility and suppression.
 * Priority uses an exploitation slot, leaving the final exploration reserve intact.
 */
export function rankBiEvidence<T>(params: {
  candidates: BiEvidenceCandidate<T>[];
  themes: BiRetrievalTheme[];
  limit: number;
  viewerRole: "coach" | "leader";
  priorityId?: string | null;
}): { items: T[]; biFilterState: "no_bi_data" | "thin" | "applied"; coachContradictionProbes: string[]; explorationIds: string[] } {
  const limit = Math.max(0, Math.floor(params.limit));
  const unique = [...new Map(params.candidates.map(c => [c.id, c])).values()];
  const themes = params.themes.filter(t => params.viewerRole === "coach" || t.leaderSafe);
  const active = themes.filter(isBiThemeRetrievalActive);
  const probes = params.viewerRole === "coach" ? [...new Set(themes.filter(t => t.contradicted || t.status === "contradicted").map(t =>
    t.contradictionProbe || "Explore evidence that challenges " + t.behaviorKeys[0].replace(/_/g, " ") + "; ask an open question before drawing a conclusion."))] : [];
  const state = !themes.length ? "no_bi_data" : !active.length ? "thin" : "applied";
  if (state === "no_bi_data") return { items: unique.slice(0, limit).map(c => c.item), biFilterState: state, coachContradictionProbes: probes, explorationIds: [] };
  const matches = (candidate: BiEvidenceCandidate<T>, theme: BiRetrievalTheme) => candidate.linkedThemeIds?.includes(theme.id)
    || candidate.behaviorKeys?.some(key => theme.behaviorKeys.some(k => normalizeBiKey(k) === normalizeBiKey(key)));
  const mayDemote = active.some(t => t.governance === "confirmed" || t.confidence >= 0.8);
  const scored = unique.map((candidate, index) => {
    const aligned = active.filter(t => matches(candidate, t));
    const excluded = themes.some(t => (t.governance === "rejected" || t.contradicted || ["contradicted", "rejected", "retired"].includes(t.status)) && matches(candidate, t));
    const contradicted = Boolean(candidate.contradicted || themes.some(t => (t.contradicted || t.status === "contradicted") && matches(candidate, t)));
    // A simultaneous active match must never override rejection or contradiction.
    const boost = excluded || contradicted ? 0 : aligned.length
      ? Math.max(...aligned.map(t => 0.5 + Math.max(0, Math.min(1, t.confidence))))
      : mayDemote ? -0.2 : 0;
    return {candidate, index, aligned: aligned.length > 0, contradicted, score: (Number.isFinite(candidate.score) ? candidate.score : 0) + boost};
  });
  const slots = Math.min(limit, scored.length);
  const reserve = Math.min(slots, Math.ceil(slots * 0.3));
  const priority = scored.find(s => s.candidate.id === params.priorityId);
  const time = (s: typeof scored[number]) => Date.parse(s.candidate.occurredAt ?? "") || 0;
  const discovery = [...scored].sort((a, b) => Number(b.contradicted) - Number(a.contradicted)
    || Number(a.aligned) - Number(b.aligned) || time(b) - time(a) || a.index - b.index);
  // If all slots are exploration (K=1), the priority itself remains in that reserve.
  const exploration = slots > reserve && priority
    ? discovery.filter(s => s !== priority).slice(0, reserve)
    : priority && slots === reserve ? [priority, ...discovery.filter(s => s !== priority)].slice(0, reserve)
    : discovery.slice(0, reserve);
  const explorationIds = new Set(exploration.map(s => s.candidate.id));
  const exploitation = [...scored].filter(s => !explorationIds.has(s.candidate.id))
    .sort((a, b) => b.score - a.score || a.index - b.index);
  const selected = priority && !explorationIds.has(priority.candidate.id)
    ? [priority, ...exploitation.filter(s => s !== priority)].slice(0, slots - reserve)
    : exploitation.slice(0, slots - reserve);
  return {items: [...selected, ...exploration].map(s => s.candidate.item), biFilterState: state,
    coachContradictionProbes: probes, explorationIds: exploration.map(s => s.candidate.id)};
}

/** Traverse only the bounded artifact -> cluster -> theme chain after governance. */
export function linkedBiThemeIds(graph: GraphSnapshot | null | undefined, sourceNodeIds: Set<string>): string[] {
  const nodes = new Map((graph?.recentNodes ?? []).filter(n => !isMemoryArchiveType(n.node_type) && !isMemoryArchiveType(n.source_type)).map(n => [n.id, n]));
  const edges = (graph?.recentEdges ?? []).filter(e => ["linked_to", "behavioral_pattern"].includes(e.edge_type) && nodes.has(e.from_node_id) && nodes.has(e.to_node_id));
  const adjacent = (id: string) => edges.flatMap(e => e.from_node_id === id ? [e.to_node_id] : e.to_node_id === id ? [e.from_node_id] : []);
  const ids = new Set<string>();
  const add = (id: string) => { const n = nodes.get(id); if (n?.source_type === "behavioral_intelligence_v2_theme") ids.add(n.source_id); };
  for (const id of sourceNodeIds) { add(id); for (const next of adjacent(id)) { add(next); if (nodes.get(next)?.source_type === "behavioral_intelligence_v2_cluster") adjacent(next).forEach(add); } }
  return [...ids].sort();
}

export function rebuildGovernedGraph(graph: GraphSnapshot, recentNodes: GraphSnapshot["recentNodes"], audience: "coach" | "leader", asOf = Date.now()): GraphSnapshot {
  const ids = new Set(recentNodes.map(n => n.id));
  const nodes = new Map(recentNodes.map(n => [n.id,n]));
  const recentEdges = audience === "leader" ? [] : (graph.recentEdges ?? [])
    .filter(e => ids.has(e.from_node_id) && ids.has(e.to_node_id))
    .map(edge => {
      const from=nodes.get(edge.from_node_id)!,to=nodes.get(edge.to_node_id)!;
      return {...edge,from_node_title:from.title,from_node_type:from.node_type,from_node_source_type:from.source_type,
        to_node_title:to.title,to_node_type:to.node_type,to_node_source_type:to.source_type};
    });
  const state = buildGraphBehavioralState(recentNodes, asOf);
  return {...graph, recentNodes, recentEdges,
    nodeCounts: recentNodes.reduce<Record<string, number>>((counts, n) => {counts[n.node_type] = (counts[n.node_type] ?? 0) + 1; return counts;}, {}),
    behavioralState: {...state, ...(audience === "leader" ? {contradictionCount: 0, contradictionSignals: []} : {})}};
}
